import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { GroupsService } from 'src/app/views/groups/groups.service';
import { ModelsService } from 'src/app/views/my-space/my-models/models.service';
import { ModelsDialogComponent } from '../models-dialog/models-dialog.component';
import { TemplateListComponent } from './template-list.component';

@Component({
  selector: 'app-template-picker',
  templateUrl: './template-picker.component.html',
  styleUrls: ['./template-picker.component.scss'],
  standalone: true,
  imports: [TranslateModule, ReactiveFormsModule, MatIconModule, MatTooltipModule, MatButtonModule],
})
export class TemplatePickerComponent implements OnInit {
  @Input() selectedTextGenerationTemplateID = '';
  @Input() selectedTranscribeTemplateID = '';
  @Input() mode: 'TextGeneration' | 'Transcription' = 'TextGeneration';
  @Output() onAIGenerationTemplateChange = new EventEmitter<string>();
  @Output() onTranscribeTemplateChange = new EventEmitter<string>();

  initDone = false;
  AIContextTemplates = [];
  loading = false;
  modes = ['TextGeneration', 'Transcription'];
  groups = [{ name: this.translate.instant('Global.None'), _id: 'private' }];
  selectedGroup = 'private';
  displayedTemplates;

  private overlay = inject(DynamicOverlayService);

  constructor(
    private modelService: ModelsService,
    private dialog: MatDialog,
    private groupService: GroupsService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.getTemplates();
    // this.getGroups();
  }

  getGroups() {
    this.groupService.getGroups().subscribe((groups) => {
      this.groups = [{ name: this.translate.instant('Global.None'), _id: 'private' }];
      groups.forEach((group) => {
        this.groups.push(group);
      });
    });
  }

  getTemplates() {
    this.modelService.getTemplates().subscribe((templates) => {
      templates = templates || [];
      this.AIContextTemplates = [...templates];
      this.initDone = true;
      this.filterTemplates();
      this.cdr.markForCheck();
    });
  }

  filterTemplates() {
    this.displayedTemplates = [{ data: null, name: this.translate.instant('Global.None'), _id: 'none' }, ...this.AIContextTemplates];
  }

  openTemplateListOverlay(trigger: MatButton): void {
    const triggerWidth = trigger._elementRef.nativeElement.offsetWidth;
    const { componentInstance } = this.overlay.open(trigger._elementRef, TemplateListComponent, {
      hasBackdrop: true,
      data: {
        templates: this.displayedTemplates,
        selectedId: this.mode === 'TextGeneration' ? this.selectedTextGenerationTemplateID : this.selectedTranscribeTemplateID,
        width: triggerWidth,
      },
    });

    componentInstance.templateSelected.pipe(take(1)).subscribe((templateId) => {
      if (this.mode === 'TextGeneration') {
        this.selectedTextGenerationTemplateID = templateId;
      } else {
        this.selectedTranscribeTemplateID = templateId;
      }
      this.aiSelectionChanged();
      this.overlay.close();
    });

    componentInstance.edit.pipe(take(1)).subscribe(() => {
      this.openEditTemplateDialog();
      this.overlay.close();
    });

    componentInstance.create.pipe(take(1)).subscribe(() => {
      this.openCreateTemplateDialog();
      this.overlay.close();
    });
  }

  openCreateTemplateDialog() {
    this.dialog
      .open(ModelsDialogComponent, {
        width: '90vw',
        maxWidth: '800px',
        data: { type: 'create' },
        disableClose: true,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.getTemplates();
        }
      });
  }

  getSelectedTemplate() {
    const templateID = this.mode === 'TextGeneration' ? this.selectedTextGenerationTemplateID : this.selectedTranscribeTemplateID;
    return this.AIContextTemplates.find((template) => template._id == templateID);
  }

  openEditTemplateDialog() {
    const template = this.getSelectedTemplate();
    if (!template || template._id == 'none') {
      return;
    }
    window.open(`/myspace/my-models/${template._id}`, '_blank');
  }

  aiSelectionChanged() {
    if (this.mode == 'TextGeneration') {
      localStorage.setItem('skz_last_selected_text_gen_template_id', this.selectedTextGenerationTemplateID || 'none');
      this.onAIGenerationTemplateChange.emit(this.selectedTextGenerationTemplateID);
    } else {
      localStorage.setItem('skz_last_selected_transcribe_template_id', this.selectedTranscribeTemplateID || 'none');
      this.onTranscribeTemplateChange.emit(this.selectedTranscribeTemplateID);
    }
  }
}
